import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { animated, useSpring } from '@react-spring/web';
import { interpolate as d3interpolate } from 'd3';

import { Comp, useTheme } from '@redsift/design-system';

import { BarDirection, BarOrientation, BarProps } from './types';
import { StyledBar } from './styles';
import { monochrome } from '../../scheme';
import { config } from '../../config';
import { BarDatum } from '../../types';

const COMPONENT_NAME = 'Bar';
const CLASSNAME = 'redsift-bar';

export const Bar: Comp<BarProps, SVGGElement> = forwardRef((props, ref) => {
  const { color = monochrome, data, index = 0, isSelected: propsIsSelected, labelDecorator, onClick, role } = props;

  const {
    className,
    disableAnimations,
    gap = 5,
    height = 40,
    maxHeight = 100,
    orientation = BarOrientation.horizontal,
    direction = BarDirection.up,
    previousData = {
      data: {
        key: '',
        value: 0,
      },
    },
    scale,
    scalePosition,
    width = 40,
    minWidth,
    ...forwardedProps
  } = props;

  const theme = useTheme();

  const getValue = (data: BarDatum['data']) =>
    data?.cumulativeValue !== undefined ? data.cumulativeValue : data?.value;
  const interpolator = d3interpolate(getValue(previousData!.data) || 0, getValue(data.data) || 0);

  const text = labelDecorator
    ? labelDecorator(data, { index, isSelected: propsIsSelected, color })
    : (data.data.key as string);
  const isSelectable = role === 'option';
  const isDeselected = isSelectable && propsIsSelected === false;
  const isHorizontal = orientation === BarOrientation.horizontal;
  const goesUp = direction === BarDirection.up;
  const isOrdinal = typeof scalePosition.domain()[0] === 'string';

  const animatedProps = useSpring({
    ...config,
    to: async (next: (props?: object) => Promise<void>) => {
      await next({ t: 1 });
    },
    from: { t: 0 },
  });

  const computedWidth: number = minWidth ? Math.max(minWidth, width) : width;

  return (
    <StyledBar
      {...forwardedProps}
      ref={ref}
      className={classNames(Bar.className, className)}
      transform={
        isHorizontal
          ? `translate(0, ${scalePosition(data.data.key as any)! + gap! / 2})`
          : `translate(${
              goesUp
                ? scalePosition(data.data.key as any)! + (isOrdinal ? computedWidth + gap! / 2 : computedWidth / 2)
                : scalePosition(data.data.key as any)! + (isOrdinal ? -computedWidth + gap! / 2 : -computedWidth / 2)
            }, ${maxHeight})${goesUp ? ', rotate(180)' : ''}`
      }
      $clickable={Boolean(onClick)}
      $theme={theme}
    >
      {isHorizontal ? (
        <animated.rect
          height={height}
          fill={isDeselected ? 'var(--redsift-color-neutral-light-grey)' : color}
          width={
            config.immediate || disableAnimations
              ? scale(data.data.value)
              : animatedProps.t.to((t) => scale(interpolator(t)))
          }
        />
      ) : (
        <animated.rect
          height={
            config.immediate || disableAnimations
              ? goesUp
                ? maxHeight! - scale(getValue(data.data))
                : scale(getValue(data.data)) - maxHeight!
              : animatedProps.t.to((t) =>
                  Math.max(0, goesUp ? maxHeight! - scale(interpolator(t)) : scale(interpolator(t)) - maxHeight!)
                )
          }
          fill={isDeselected ? 'var(--redsift-color-neutral-light-grey)' : color}
          width={computedWidth}
        />
      )}
      {isHorizontal ? (
        <text x="10" y={height! / 2} dy="0.35em" aria-hidden={true}>
          {text}
        </text>
      ) : null}
    </StyledBar>
  );
});
Bar.className = CLASSNAME;
Bar.displayName = COMPONENT_NAME;
